import { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import Swal from "sweetalert2";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useSelector } from "react-redux";
import { uploadImage } from "services/imageUpload";
import { useGetBannersQuery } from "slices/cms/homeSlice";
import { useAddbannersMutation } from "slices/cms/homeSlice";
import { IMAGE_URL } from "network";

function Home() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [update] = useAddbannersMutation();
  const [initialValues, setInitialValues] = useState({
    image1: "",
    image2: "",
    image3: "",
    video: "",
    appHomeBanner: "",
  });
  const auth = useSelector((state) => state.auth.auth?.user);
  const { data, isLoading, refetch } = useGetBannersQuery({
    page,
    limit,
    search,
  });

  // helper to check image dimensions
  const checkDimensions = (
    file,
    expectedWidth,
    expectedHeight,
    flexibleHeight = false,
  ) =>
    new Promise((resolve, reject) => {
      if (file.type === "image/svg+xml") {
        resolve(true); // allow SVGs without checking dimensions
        return;
      }
      if (file.type === "image/png") {
        resolve(true); // allow SVGs without checking dimensions
        return;
      }
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        if (
          img.width === expectedWidth &&
          (flexibleHeight
            ? img.height >= expectedHeight.min &&
              img.height <= expectedHeight.max
            : img.height === expectedHeight)
        ) {
          resolve(true);
        } else {
          reject(false);
        }
      };
    });

  // formik
  const formik = useFormik({
    enableReinitialize: true, // important to allow updates when initialValues change
    initialValues: initialValues,
    validationSchema: Yup.object().shape({
      image1: Yup.string().required("Image 1 is required"),
      image2: Yup.string().required("Image 2 is required"),
      image3: Yup.string().required("Image 3 is required"),
      video: Yup.string().required("Video is required"),
      appHomeBanner: Yup.string().required("App Home Banner is required"),
    }),
    onSubmit: async (values) => {
      // const payload = [
      //   { type: "image", url: values.image1, order: 1 },
      //   { type: "image", url: values.image2, order: 2 },
      //   { type: "image", url: values.image3, order: 3 },
      //   { type: "video", url: values.video, order: 4 },
      // ];

      const payload = {
        banners: [
          { type: "image", url: values.image1, order: 1 },
          { type: "image", url: values.image2, order: 2 },
          { type: "image", url: values.image3, order: 3 },
          { type: "video", url: values.video, order: 4 },
        ],
        appHomeBanner: values.appHomeBanner, // 👈 OUTSIDE images array
      };

      try {
        const response = await update(payload).unwrap(); // unwrap to catch errors
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Banners updated successfully!",
          confirmButtonColor: "#3085d6",
        });
        formik.resetForm();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.data?.message || "Something went wrong!",
        });
      }
    },
  });

  useEffect(() => {
    console.log("data", data);
    if (data?.homePageBanners && data?.homePageBanners.length) {
      const values = {
        image1: data.homePageBanners.find((item) => item.order === 1)?.url || "",
        image2: data.homePageBanners.find((item) => item.order === 2)?.url || "",
        image3: data.homePageBanners.find((item) => item.order === 3)?.url || "",
        video: data.homePageBanners.find((item) => item.order === 4)?.url || "",
        appHomeBanner: data.appHomeBanner || "",
      };
      setInitialValues(values);
      formik.setValues(values); 
    }
  }, [data]);
  // reusable upload card
  const UploadCard = ({ label, name, accept }) => (
    <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          {label}
        </Typography>
        <Box
          sx={{
            border: "2px dashed #ccc",
            borderRadius: 2,
            p: 2,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: "#fafafa",
          }}
          onClick={() => document.getElementById(name).click()}
        >
          <input
            id={name}
            name={name}
            type="file"
            accept={accept}
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.currentTarget.files[0];
              if (!file) return;

              try {
                // ✅ Skip dimension check for App Home Banner
                if (name !== "appHomeBanner") {
                  await checkDimensions(
                    file,
                    name === "image1" ? 260 : 217,
                    name === "image1" ? 491 : 235,
                  );
                }

                const uploadedName = await uploadImage(file);
                formik.setFieldValue(name, uploadedName);
              } catch (err) {
                formik.setFieldError(name, `${label} has invalid dimensions`);
              }
            }}
          />
          {formik.values[name] ? (
            <img
              src={IMAGE_URL + formik.values[name]}
              alt={name}
              crossOrigin="anonmyous"
              style={{
                maxWidth: "100%",
                maxHeight: 200,
                borderRadius: 8,
                objectFit: "cover",
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Click to upload image
            </Typography>
          )}
        </Box>
        {formik.errors[name] && formik.touched[name] && (
          <Typography color="error" variant="caption">
            {formik.errors[name]}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const UploadVideoCard = ({ label, name }) => (
    <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          {label}
        </Typography>
        <Box
          sx={{
            border: "2px dashed #ccc",
            borderRadius: 2,
            p: 2,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: "#fafafa",
          }}
          onClick={() => document.getElementById(name).click()}
        >
          <input
            id={name}
            name={name}
            type="file"
            accept="video/*"
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.currentTarget.files[0];
              if (file) {
                try {
                  const uploadedVideo = await uploadImage(file); // same API you use for images
                  formik.setFieldValue(name, uploadedVideo);
                } catch (err) {
                  formik.setFieldError(name, `${label} upload failed`);
                }
              }
            }}
          />
          {formik.values[name] ? (
            <video
              src={IMAGE_URL + formik.values[name]}
              controls
              crossOrigin="anonmyous"
              style={{
                maxWidth: "100%",
                maxHeight: 200,
                borderRadius: 8,
                objectFit: "cover",
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Click to upload video
            </Typography>
          )}
        </Box>
        {formik.errors[name] && formik.touched[name] && (
          <Typography color="error" variant="caption">
            {formik.errors[name]}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3} p={3}>
          {/* Images */}
          <Grid item xs={12} md={4}>
            <UploadCard
              label="Upload Image 1 (260x491)"
              name="image1"
              accept="image/*"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <UploadCard
              label="Upload Image 2 (217x235)"
              name="image2"
              accept="image/*"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <UploadCard
              label="Upload Image 3 (217x235)"
              name="image3"
              accept="image/*"
            />
          </Grid>

          {/* YouTube Link */}
          <Grid item xs={12}>
            <UploadVideoCard label="Upload Video" name="video" />
          </Grid>

          <Grid item xs={12} md={4}>
            <UploadCard
              label="Upload App Home Banner"
              name="appHomeBanner"
              accept="image/*"
            />
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ py: 1.5, fontSize: "16px", fontWeight: "bold" }}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </form>

      <Footer />
    </DashboardLayout>
  );
}

export default Home;
